import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { refusalRedirectFor } from '../../utils/routeAccess';
import { useRefusalToast } from './useRefusalToast';

interface CareTeamRouteProps {
  children: React.ReactNode;
}

/**
 * CareTeamRoute — protects routes that require care-team-member access.
 *
 * Mirrors the server-side `@auth(roles: ["careTeamMember"])` gate used by
 * YOJO-1727's broadcast mutations. Today the care-portal Role enum does not
 * have an explicit `careTeamMember` role, so we treat the union of `nurse`
 * and `admin` as care-team membership — matching the existing access pattern
 * used elsewhere in the portal. Centralised here so the role-mapping logic
 * has exactly one source of truth.
 */
const CareTeamRoute: React.FC<CareTeamRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isAdmin, isNurse, loading: rolesLoading } = useCurrentUser();
  const location = useLocation();
  const isAuthorized = isAdmin || isNurse;

  // The permission toast fires from an effect, once per refusal (see useRefusalToast)
  useRefusalToast(
    'You do not have permission to access this page. Care-team-member role required.',
    !authLoading && !rolesLoading && !!user && !isAuthorized
  );

  if (authLoading || rolesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  if (!isAuthorized) {
    return <Navigate to={refusalRedirectFor(roles)} replace />;
  }

  return <>{children}</>;
};

export default CareTeamRoute;
